import { createFileRoute } from "@tanstack/react-router";
import { alternateLinkList } from "@/lib/i18n/locale";
import { Link as RouterLink } from "@tanstack/react-router";
import { ArrowRight, Link2, Mail, QrCode, UserPlus, Wallet, Lock, Signature } from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import heroImg from "@/assets/sharing-hero-tools.webp";
import walletImg from "@/assets/sharing-wallet.webp";
import leadImg from "@/assets/sharing-lead-capture.webp";

const IMG: Record<string, string> = { hero: heroImg, wallet: walletImg, lead: leadImg };

export const Route = createFileRoute("/platform/sharing")({
  head: () => ({
    meta: [
      { title: "Sharing & Networking Tools — banana.cards" },
      { name: "description", content: "NFC, QR, links, Wallet passes and email signatures. One profile, every share channel." },
      { property: "og:title", content: "Sharing & Networking Tools — banana.cards" },
      { property: "og:description", content: "NFC, QR, links, Wallet passes and email signatures. One profile, every share channel." },
      { property: "og:image", content: IMG['hero'] },
    ],
    links: alternateLinkList("/platform/sharing", "en"),
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="Sharing & Networking Tools"
      title="Share who you are,"
      italic="anywhere."
      description="Exchange contact details via NFC, QR, links, Wallet passes and email signatures. One profile, infinite ways to share — across every device."
      heroImage={IMG['hero']}
      heroImageAlt="All the ways to share a Banana business card: NFC tap, QR code, short link, Wallet pass and email signature"
      metrics={[{ v: "9", l: "share channels" }, { v: "0", l: "app installs" }, { v: "100%", l: "cross-platform" }]}
      bullets={[
        { icon: QrCode, title: "Dynamic QR codes", desc: "Update the destination anytime without reprinting. Track scans, devices and locations." },
        { icon: Link2, title: "Short links", desc: "Custom short URLs with click analytics — perfect for bio links and campaigns." },
        { icon: Mail, title: "Email signature", desc: "Drop your card into every email with a one-line signature builder." }
      ]}
      deepDives={[
        { eyebrow: "Wallet", title: "Add to Apple & Google", italic: "Wallet in one tap.", description: "Recipients save your card to their phone wallet for instant re-access — no app, no scrolling through messages.", imageAlt: "Banana digital business card added to Apple Wallet and Google Wallet on a smartphone lock screen", image: IMG["wallet"], reverse: false, points: ["Apple Wallet pass with auto-updates", "Google Wallet pass", "Live updates when you change your profile", "Lock screen accessibility"] },
        { eyebrow: "Events", title: "Captured leads, instantly", italic: "organized.", description: "Use event-specific QR codes to capture leads at expos, conferences and meetups. Every scan flows into your CRM with the event tagged automatically.", imageAlt: "Event-scoped QR code capturing leads with instant CRM tagging and custom lead-capture form per event", image: IMG["lead"], reverse: true, points: ["Event-scoped QR codes", "Auto-tagging in CRM", "Custom lead-capture form per event", "CSV & Zapier export"] }
      ]}
      howItWorks={[
        { title: "Generate a share", desc: "NFC tap, QR, link, signature or Wallet." },
        { title: "Recipient receives", desc: "Profile opens in their browser instantly." },
        { title: "They tap save", desc: "Card lands in their contacts or wallet." },
        { title: "Stays in sync", desc: "Your updates push to their saved copy." }
      ]}
      capabilities={[
        "Unlimited QR codes",
        "Custom branded short links",
        "Apple & Google Wallet",
        "Email signature builder",
        "Event-scoped tracking",
        "Per-channel analytics",
        "Geo & device insights",
        "UTM passthrough"
      ]}
      faq={[
        { q: "How many ways can I share my Banana card?", a: "At least seven: NFC tap (physical card to phone), dynamic QR code (printed, screenshot, on a slide), short shareable link, Apple/Google Wallet pass, email signature block, AirDrop / native share sheet, and direct messaging into WhatsApp, iMessage, LinkedIn or SMS. Every channel opens the same live profile." },
        { q: "Can I track who viewed my card and where they came from?", a: "Yes — every view, tap and scan is logged with timestamp, device, browser, approximate location and traffic source (QR vs link vs Wallet vs signature). Analytics breaks views down by card, by team member and by campaign so you can see what's actually working." },
        { q: "Can I change my card without reprinting or reshipping?", a: "Yes — every share method points to your live profile. Update your title, photo, links or call-to-action once and every QR, NFC, Wallet pass and signature reflects it immediately. Dynamic QR codes never expire." },
        { q: "Can recipients save my contact without an app or signup?", a: "Yes — your card includes a one-tap 'Save contact' button that adds a vCard directly to their phone's address book, and an 'Add to Apple/Google Wallet' button so your card lives in their wallet forever." },
        { q: "Can I scan someone else's paper business card into Banana?", a: "Yes — the AI card scanner in the mobile app captures a paper card from a photo, extracts the name, company, title, email, phone, website and address, and creates a clean, deduplicated contact in your CRM." },
        { q: "Does sharing work without the recipient having internet?", a: "An NFC tap or QR scan needs a brief internet connection to load the profile, but once they save your contact to their address book or your card to Wallet, everything (phone, email, vCard data) remains accessible offline." }
      ]}
      outro={ { title: "Share once.", italic: "Stay top of mind.", description: "Every channel, every device — connected to one profile." } }
      afterFaq={<NetworkingToolsGrid />}
    />
  );
}

const TOOLS = [
  { title: "Contact Exchange Form", desc: "Turn every card view into a captured lead.", href: "/platform/networking/lead-exchange", icon: UserPlus },
  { title: "Welcome Email", desc: "Auto-send a branded welcome the moment someone connects.", href: "/platform/networking/welcome-email", icon: Mail },
  { title: "Apple & Google Wallet", desc: "Save your card to any phone wallet, always in sync.", href: "/platform/networking/wallet", icon: Wallet },
  { title: "PIN Protection", desc: "Lock your card with a 4-digit PIN for private sharing.", href: "/platform/networking/pin-protection", icon: Lock },
  { title: "Dynamic QR Code", desc: "Branded, trackable, re-routable QR codes per card.", href: "/platform/networking/qr-code", icon: QrCode },
  { title: "Email Signature", desc: "Generate a polished signature for Gmail, Outlook & Mail.", href: "/platform/networking/email-signature", icon: Signature },
];

function NetworkingToolsGrid() {
  return (
    <section className="bg-background">
      <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
        <div className="mb-10 max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Networking tools</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
            Every share channel, <em className="italic">covered.</em>
          </h2>
          <p className="mt-4 max-w-md text-base text-muted-foreground">
            Six purpose-built tools that turn every tap into a real conversation. Use one, use them all.
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {TOOLS.map((t) => {
            const Icon = t.icon;
            return (
              <RouterLink
                key={t.href}
                to={t.href}
                aria-label={`${t.title} — learn more about this networking tool`}
                className="group flex flex-col rounded-2xl bg-muted p-6 transition hover:-translate-y-0.5"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-zinc-50">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-5 text-lg font-bold">{t.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{t.desc}</p>
                <span className="mt-6 inline-flex items-center gap-1 text-sm font-bold underline-offset-4 group-hover:underline">
                  Learn more <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                </span>
              </RouterLink>
            );
          })}
        </div>
      </div>
    </section>
  );
}
